/* eslint-disable tsdoc/syntax */
import React, {
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { ListItemDecorator } from "@mui/joy";
import FormLabel from "@mui/joy/FormLabel";
import Option from "@mui/joy/Option";
import Select, { SelectOption } from "@mui/joy/Select";
import { CircularProgress } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { Placement, PopperOptions } from "popper.js";

import Check from "@zenith/assets/icons/Check";
import ChevronDown from "@zenith/assets/icons/ChevronDown.js";
import ChevronUp from "@zenith/assets/icons/ChevronUp.js";
import { SelectItemsType } from "./interfaces.js";

/**
 * Props for Select Component.
 * @interface
 * @name SelectProps
 * @kind interface
 */
interface SelectProps {
  /**
   * Array of items.
   * @property
   * @name items
   * @kind property
   * @memberof SelectProps
   * @type {SelectItemsType[]}
   */
  items: SelectItemsType[];
  /**
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof SelectProps
   * @type {string}
   */
  className: string;
  /**
   * (optional)
   * Standard HTML Attribute, for label classes.
   * @property
   * @name labelClassName
   * @kind property
   * @memberof SelectProps
   * @type {string | undefined}
   */
  labelClassName?: string;
  /**
   * Label for the Select Component.
   * @property
   * @name label
   * @kind property
   * @memberof SelectProps
   * @type {string}
   */
  label: string;
  /**
   * Id for the Select Component.
   * @property
   * @name id
   * @kind property
   * @memberof SelectProps
   * @type {string}
   */
  id: string;
  /**
   * Id for the label for the Select Component.
   * @property
   * @name labelId
   * @kind property
   * @memberof SelectProps
   * @type {string}
   */
  labelId: string;
  /**
   * Default value for the Select Component.
   * @default ""
   * @property
   * @name defaultValue
   * @kind property
   * @memberof SelectProps
   * @type {string}
   */
  defaultValue?: string;
  /**
   * (optional)
   * Standard HTML Attribute, input placeholder.
   * @default "Select"
   * @property
   * @name placeholder
   * @kind property
   * @memberof SelectProps
   * @type {string | undefined}
   */
  placeholder?: string;
  /**
   * (optional)
   * Standard HTML Attribute, input value.
   * @property
   * @name value
   * @kind property
   * @memberof SelectProps
   * @type {string | undefined}
   */
  value?: string;
  /**
   * (optional)
   * If `true`, the error message is shown.
   * @default false
   * @property
   * @name hasError
   * @kind property
   * @memberof SelectProps
   * @type {boolean | undefined}
   */
  hasError?: boolean;
  /**
   * (optional)
   * The error message.
   * @property
   * @name errorMessage
   * @kind property
   * @memberof SelectProps
   * @type {string | undefined}
   */
  errorMessage?: string;
  /**
   * (optional)
   * Icon color for the Select Component.
   * @property
   * @name iconColor
   * @kind property
   * @memberof SelectProps
   * @type {string | undefined}
   */
  iconColor?: string;
  /**
   * Callback fired when the value changes.
   * @method
   * @name onChange
   * @kind property
   * @memberof SelectProps
   * @param {React.SyntheticEvent | null} event The event source of the callback.
   * @param {string | null} newValue The new value of the component.
   * @param {string=} label The label shown on the component.
   * @returns {void}
   */
  onChange: (
    event: React.SyntheticEvent | null,
    newValue: string | null,
    label?: string
  ) => void;
  /**
   * (optional)
   * Callback fired when the popup requests to be closed.
   * @method
   * @name onClose
   * @kind property
   * @memberof SelectProps
   */
  onClose?: (newValue: string) => void;
  /**
   * (optional)
   * Z-Index for the listbox
   * (needed so the listbox is not behind another element, e.g.: modal).
   * @property
   * @name listboxZIndex
   * @kind property
   * @memberof SelectProps
   * @type {number | undefined}
   */
  listboxZIndex?: number;
  /**
   * (optional)
   * Text to display when there are no options.
   * @property
   * @name noOptionsText
   * @kind property
   * @memberof SelectProps
   * @type {string | undefined}
   */
  noOptionsText?: string;
  /**
   * (optional)
   * If `true`, shows the 'check' icon to the selected item.
   * @default false
   * @property
   * @name showSelectedValueCheckIcon
   * @kind property
   * @memberof SelectProps
   * @type {boolean | undefined}
   */
  showSelectedValueCheckIcon?: boolean;
  /**
   * (optional)
   * If `true`, shows a divider on the top of the select when opened.
   * @default false
   * @property
   * @name showDivider
   * @kind property
   * @memberof SelectProps
   * @type {boolean | undefined}
   */
  showDivider?: boolean;
  /**
   * (optional)
   * If `true`, shows the loading icon.
   * @default false
   * @property
   * @name isLoading
   * @kind property
   * @memberof SelectProps
   * @type {boolean | undefined}
   */
  isLoading?: boolean;
  /**
   * (optional)
   * If `true`, disables the Select Component.
   * @default false
   * @property
   * @name disabled
   * @kind property
   * @memberof SelectProps
   * @type {boolean | undefined}
   */
  disabled?: boolean;
  /**
   * (optional)
   * If `true`, the select field is editable.
   * @default true
   * @property
   * @name editable
   * @kind property
   * @memberof SelectProps
   * @type {boolean | undefined}
   */
  editable?: boolean;
  /**
   * (optional)
   * If `true`, shows a search box at the top of the list to filter long lists.
   * @default false
   * @property
   * @name searchable
   * @kind property
   * @memberof SelectProps
   * @type {boolean | undefined}
   */
  searchable?: boolean;
}

// Fired when a select opens its list, so any other open select closes —
// Joy's Select doesn't close one listbox when another opens.
const SELECT_OPEN_EVENT = "zenith-select-open";

/**
 * Memoized Select Component.
 * @constant
 * @name SelectComponent
 * @kind variable
 * @type {React.NamedExoticComponent<SelectProps>}
 */
const SelectComponent: React.NamedExoticComponent<SelectProps> = memo(
  /**
   * Select Component.
   * @function
   * @name SelectComponent
   * @kind function
   * @memberof SelectComponent
   * @param {SelectProps} props Props for Select Component.
   * @returns {JSX.Element} Select Component.
   */
  function SelectComponent(props: SelectProps): JSX.Element {
    // This useState is here so we can pass the label value to the onChange function
    const [item, setItem] = useState<{ value: string; label: string }>({
      value: "",
      label: "",
    });

    const [isListOpen, setIsListOpen] = useState(false);
    const instanceId = useId();

    const [search, setSearch] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchTerm = props.searchable ? search.trim().toLowerCase() : "";
    const matchesSearch = (label: string) =>
      !searchTerm || label.toLowerCase().includes(searchTerm);
    const hasSearchResults = props.items.some((i) => matchesSearch(i.label));

    // Start each opening with an empty search, ready to type into
    useEffect(() => {
      if (!props.searchable) return;
      if (!isListOpen) {
        setSearch("");
        return;
      }
      const timer = window.setTimeout(() => searchInputRef.current?.focus());
      return () => window.clearTimeout(timer);
    }, [isListOpen, props.searchable]);

    useEffect(() => {
      const closeIfOtherOpened = (event: Event) => {
        if ((event as CustomEvent<string>).detail !== instanceId) {
          setIsListOpen(false);
        }
      };
      window.addEventListener(SELECT_OPEN_EVENT, closeIfOtherOpened);
      return () =>
        window.removeEventListener(SELECT_OPEN_EVENT, closeIfOtherOpened);
    }, [instanceId]);
    const [popperPlacement, setPopperPlacement] = useState<
      Placement | undefined
    >();
    const popperPlacementRef = useRef<Placement | undefined>(undefined);

    const [valueFieldClassName, setValueFieldClassName] = useState(
      props.defaultValue ? "select-value" : ""
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const [listboxWidth, setListboxWidth] = useState(0);

    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const ro = new ResizeObserver(() => setListboxWidth(el.offsetWidth));
      ro.observe(el);
      setListboxWidth(el.offsetWidth);
      return () => ro.disconnect();
    }, []);

    const isEditable = props.editable !== false; // Default to true unless explicitly set to false

    const popperOptions = useMemo(
      () => ({
        modifiers: [
          {
            name: "offset",
            options: { offset: [0, 0] },
          },
          {
            name: "positionLogger",
            enabled: true,
            phase: "write",
            fn: ({ state }: { state: PopperOptions }) => {
              if (popperPlacementRef.current !== state.placement) {
                popperPlacementRef.current = state.placement;
                setPopperPlacement(state.placement);
              }
            },
          },
          {
            name: "sameWidth",
            enabled: true,
            phase: "beforeWrite",
            requires: ["computeStyles"],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            fn: ({ state }: { state: any }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            effect: ({ state }: { state: any }) => {
              state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
            },
          },
        ],
      }),
      []
    );

    const renderValue = (item: SelectOption<string> | null) => {
      if (!item) {
        return null;
      }

      return (
        <React.Fragment>
          <ListItemDecorator>
            {props.items.find((i) => i.value === item.value)?.icon}
          </ListItemDecorator>
          {item.label}
        </React.Fragment>
      );
    };

    return (
      <Box>
        <FormControl fullWidth>
          <FormLabel
            className={`label ${props.labelClassName}`}
            id={props.labelId}
            htmlFor={props.id}
          >
            {props.label}
          </FormLabel>
          <div className="relative" ref={containerRef}>
            {props.showDivider && isListOpen ? (
              <div
                className={`absolute left-1/2 z-10 w-4/5 -translate-x-1/2 border-t-1 border-solid border-neutral-200 focus:outline-neutral-900 ${
                  popperPlacement === "top" ? "top-0" : "bottom-0"
                }`}
              ></div>
            ) : (
              ""
            )}
            <Select
              className={`select-field border-neutral-300 focus-visible:outline-neutral-900 ${
                props.className
              } ${valueFieldClassName} ${
                props.hasError ? "border-red-500" : ""
              } ${
                props.disabled ? "bg-neutral-25" : "bg-white"
              } [&:has(button:disabled)]:bg-neutral-25`}
              indicator={
                isEditable ? (
                  props.isLoading ? (
                    <CircularProgress
                      className={"text-red-500 "}
                      size={"20px"}
                    />
                  ) : isListOpen ? (
                    <ChevronUp color={props.iconColor} />
                  ) : (
                    <ChevronDown color={props.iconColor} />
                  )
                ) : null
              }
              disabled={props.isLoading || props.disabled || !isEditable}
              defaultValue={props.defaultValue}
              placeholder={
                props.isLoading ? " " : props.placeholder || "Select"
              }
              onChange={(e) => {
                if (isEditable) {
                  props.onChange(e, item.value, item.label);
                  if (item.value) setValueFieldClassName("select-value");
                }
              }}
              renderValue={renderValue}
              value={props.value}
              listboxOpen={
                isListOpen && !props.disabled && !props.isLoading
              }
              onListboxOpenChange={(open) => {
                setIsListOpen(open);
                if (open) {
                  window.dispatchEvent(
                    new CustomEvent(SELECT_OPEN_EVENT, { detail: instanceId })
                  );
                }
              }}
              onClose={() => (props.onClose ? props.onClose(item.value) : null)}
              slotProps={{
                root: {
                  style: {
                    borderTopWidth:
                      isListOpen && popperPlacement === "top" ? 0 : 1,
                    borderTopLeftRadius:
                      isListOpen && popperPlacement === "top" ? 0 : 10,
                    borderTopRightRadius:
                      isListOpen && popperPlacement === "top" ? 0 : 10,
                    borderBottomWidth:
                      isListOpen && popperPlacement === "bottom" ? 0 : 1,
                    borderBottomLeftRadius:
                      isListOpen && popperPlacement === "bottom" ? 0 : 10,
                    borderBottomRightRadius:
                      isListOpen && popperPlacement === "bottom" ? 0 : 10,
                    borderSpacing: "20px",
                  },
                },
                listbox: {
                  style: {
                    zIndex: props.listboxZIndex,
                    borderColor: "#AFAFAE",
                    borderTopWidth: popperPlacement === "bottom" ? 0 : 1,
                    borderTopLeftRadius: popperPlacement === "bottom" ? 0 : 10,
                    borderTopRightRadius: popperPlacement === "bottom" ? 0 : 10,
                    borderBottomWidth: popperPlacement === "top" ? 0 : 1,
                    borderBottomLeftRadius: popperPlacement === "top" ? 0 : 10,
                    borderBottomRightRadius: popperPlacement === "top" ? 0 : 10,
                    boxShadow: "none",
                    overflow: "auto",
                    ...(listboxWidth > 0 && {
                      width: `${listboxWidth}px`,
                      minWidth: "unset",
                      maxWidth: `${listboxWidth}px`,
                      boxSizing: "border-box" as const,
                    }),
                  },
                  popperOptions,
                },
                button: {
                  id: props.id,
                  // TODO: Material UI set aria-labelledby correctly & automatically
                  // but Base UI and Joy UI don't yet.
                  "aria-labelledby": `${props.id} ${props.labelId}`,
                  className: props.disabled ? "Mui-disabled" : "",
                },
              }}
              sx={{
                "--Select-minWidth": "0px",
                width: "100%",
                // changes the placeholder value color
                "button.MuiSelect-button": {
                  color: "#C6C6C6",
                  width: "100%",
                },
                "button.MuiSelect-button.Mui-disabled": {
                  color: "rgb(155 155 155 / var(--tw-text-opacity))",
                },
                // changes selected value color
                "button.MuiSelect-button:has(span)": {
                  color: isListOpen ? "#C6C6C6" : "#1A1C1F",
                },
                // changes focus color
                "&.Mui-focusVisible": {
                  "--Select-indicatorColor": "#1A1C1F",
                  "--Select-focusedHighlight": "#1A1C1F",
                },
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  backgroundColor: "rgb(249 249 249 / 1)",
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: "rgb(230 230 230 / 1)",
                    boxShadow: "1px 1px 8px 1px rgb(230 230 230 / .5)",
                  },
                },
              }}
            >
              {props.searchable && props.items.length > 0 && (
                <div className="sticky top-0 z-10 bg-white px-3 pb-2 pt-1">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    placeholder="Search"
                    aria-label={`Search ${props.label}`}
                    className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-900 outline-none placeholder:text-neutral-200 focus:border-neutral-900"
                    onChange={(e) => setSearch(e.target.value)}
                    // Keep keys for the text box; the list would otherwise
                    // use them to jump between or pick options. Escape
                    // still closes the list.
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") e.stopPropagation();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              {props.searchable && props.items.length > 0 && !hasSearchResults && (
                <span className="mx-3 my-2 block text-sm text-neutral-500">
                  No results found
                </span>
              )}
              {props.items.length > 0 ? (
                props.items.map((item, index: number) => (
                  <Option
                    className="select-option focus:outline-none"
                    key={index}
                    value={item.value}
                    // Non-matches stay mounted (hidden) so the selected
                    // value keeps showing while the list is filtered
                    disabled={!matchesSearch(item.label)}
                    sx={matchesSearch(item.label) ? undefined : { display: "none" }}
                    slotProps={{
                      root: {
                        onClick: () => {
                          setItem({
                            value: item.value,
                            label: item.label,
                          });
                        },
                      },
                    }}
                  >
                    <div className="flex flex-row items-center justify-center">
                      {item.icon ? (
                        <ListItemDecorator className="-mr-4">
                          {item.icon}
                        </ListItemDecorator>
                      ) : (
                        ""
                      )}
                      <span className="mr-auto">
                        {item.label}
                        {item.subLabel ? (
                          <span className="ml-1" style={{ color: "#9ca3af" }}>
                            {item.subLabel}
                          </span>
                        ) : null}
                      </span>
                      {props.showSelectedValueCheckIcon &&
                        isListOpen &&
                        props.value &&
                        props.value === item.value && <Check />}
                    </div>
                  </Option>
                ))
              ) : (
                <span
                  className={`ml-3 mr-3 max-w-200  overflow-ellipsis whitespace-pre-wrap`}
                >
                  {props.noOptionsText || "No Options Available"}
                </span>
              )}
            </Select>
            {props.hasError && (
              <div className="ml-2 text-primary-250">
                <span className="break-words text-xs">
                  {props.errorMessage}
                </span>
              </div>
            )}
          </div>
        </FormControl>
      </Box>
    );
  }
);

export default SelectComponent;
